import { useLang } from "@/context/LanguageContext";
import { ARTIST_NAME, RELEASES, IMG } from "@/data/content";
import { Reveal, PageHero, Tilt, ImageReveal, usePageTitle } from "@/components/Shared";

/* Covers as art objects: oversized, tilted, overlapping giant outline titles */
const DiscographyPage = () => {
  const { t, lf } = useLang();
  usePageTitle(`${t.nav.discography} — ${ARTIST_NAME}`);

  return (
    <div data-testid="discography-page">
      <PageHero eyebrow={t.disco.eyebrow} title={t.disco.title} img={IMG.discography} />

      <section className="overflow-hidden px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <div className="space-y-36 sm:space-y-48">
          {RELEASES.map((r, i) => (
            <div key={r.id} data-testid="discography-album-item" className="relative">
              {/* giant background title */}
              <span
                className={`pointer-events-none absolute -top-14 select-none whitespace-nowrap font-serif text-[15vw] leading-none text-outline opacity-50 sm:text-[11vw] ${
                  i % 2 === 1 ? "right-0" : "left-0"
                }`}
              >
                {r.title}
              </span>

              <div className="relative z-10 grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2 lg:col-start-8" : "lg:col-start-1"}`}>
                  <Reveal>
                    <Tilt max={11}>
                      <div className={`${i % 2 === 1 ? "rotate-2" : "-rotate-2"} shadow-[0_90px_140px_-40px_rgba(0,0,0,0.9)]`}>
                        <ImageReveal src={r.cover} alt={r.title} className="aspect-square w-full" delay={0.1} />
                      </div>
                    </Tilt>
                  </Reveal>
                </div>

                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1 lg:col-start-1" : "lg:col-start-7"}`}>
                  <Reveal>
                    <div className="flex items-baseline gap-6">
                      <span className="font-serif text-6xl font-light text-outline-gold sm:text-7xl">{String(i + 1).padStart(2, "0")}</span>
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#C5A059]">{lf(r.format)} · {r.year}</p>
                        <h2 className="mt-2 font-serif text-5xl font-light leading-none tracking-tight text-[#F9F8F6] sm:text-6xl">
                          {r.title}
                        </h2>
                      </div>
                    </div>
                  </Reveal>

                  <Reveal delay={0.1}>
                    <p className="mt-10 max-w-lg font-sans text-sm leading-loose text-[#A1A1AA]">{lf(r.description)}</p>
                  </Reveal>

                  <Reveal delay={0.12}>
                    <p className="mb-4 mt-12 font-mono text-[10px] uppercase tracking-[0.35em] text-[#71717A]">{t.disco.tracksLabel}</p>
                    <ol className="max-w-lg">
                      {r.tracks.map((tr, ti) => (
                        <li key={tr} className="group flex cursor-default items-baseline gap-6 border-b border-white/5 py-3.5">
                          <span className="font-mono text-[10px] text-[#C5A059]/50">{String(ti + 1).padStart(2, "0")}</span>
                          <span className="font-serif text-2xl font-light text-[#A1A1AA] transition-all duration-400 group-hover:translate-x-2 group-hover:text-[#F9F8F6]">
                            {tr}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </Reveal>

                  <Reveal delay={0.18}>
                    <div className="mt-10 grid gap-8 sm:grid-cols-2">
                      <div>
                        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.35em] text-[#71717A]">{t.disco.releaseLabel}</p>
                        <p className="font-sans text-xs leading-relaxed text-[#A1A1AA]">{lf(r.date)}</p>
                        <p className="font-mono text-[10px] tracking-[0.2em] text-[#71717A]">{r.catalog}</p>
                      </div>
                      <div>
                        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.35em] text-[#71717A]">{t.disco.creditsLabel}</p>
                        <p className="font-sans text-xs leading-relaxed text-[#71717A]">{lf(r.credits)}</p>
                      </div>
                    </div>
                  </Reveal>

                  <Reveal delay={0.24}>
                    <p className="mb-4 mt-10 font-mono text-[10px] uppercase tracking-[0.35em] text-[#71717A]">{t.disco.streamingLabel}</p>
                    <div className="flex flex-wrap gap-3">
                      {r.links.map((s) => (
                        <a
                          key={s.label}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`stream-${r.id}-${s.label.toLowerCase().replace(/\s/g, "")}`}
                          data-cursor="OPEN"
                          className="border border-white/15 px-6 py-2.5 font-sans text-[10px] font-medium uppercase tracking-[0.25em] text-[#A1A1AA] transition-all duration-400 hover:border-[#C5A059] hover:text-[#C5A059]"
                        >
                          {s.label}
                        </a>
                      ))}
                    </div>
                  </Reveal>
                </div>
              </div>
            </div>
          ))}
        </div>
        <Reveal className="mt-24">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">{t.disco.note}</p>
        </Reveal>
      </section>
    </div>
  );
};

export default DiscographyPage;
